/**
 * Temporal Satellite Image Comparison Page
 * Compare satellite imagery of the same location at different time periods
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaSatellite, 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaChartBar, 
  FaCheckCircle,
  FaRedo,
  FaLeaf,
  FaTree
} from 'react-icons/fa';
import ComparisonViewer from './ComparisonViewer';
import LocationSelector from './LocationSelector';
import DateSelector from './DateSelector';
import ComparisonStats from './ComparisonStats';
import CustomAlert from './CustomAlert';
import satelliteComparisonService from '../../services/satelliteComparisonService';
import sentinelHubService from '../../services/sentinelHubService';
import testComparisonService from '../../services/testComparisonService';  // TEMPORARY - TEST ONLY
import './TemporalComparisonPage.css';
import { FaHome } from 'react-icons/fa';

const TemporalComparisonPage = () => {
  const navigate = useNavigate();

  // State management
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [date1, setDate1] = useState('');
  const [date2, setDate2] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serviceHealth, setServiceHealth] = useState(null);
  const [step, setStep] = useState(1); // Wizard steps: 1=location, 2=dates, 3=results
  const [imageSource, setImageSource] = useState('sentinel'); // 'sentinel' or 'planet'
  
  // AI Analysis state - Segmentation Approach (Primary Method)
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Custom Alert state
  const [alertConfig, setAlertConfig] = useState(null);

  const showAlert = (message, type = 'warning') => {
    setAlertConfig({ message, type });
  };

  const closeAlert = () => {
    setAlertConfig(null);
  };

  /**
   * Check service health on mount
   */
  useEffect(() => {
    window.scrollTo(0, 0);
    checkServiceHealth();
  }, []);

  const checkServiceHealth = async () => {
    try {
      // Check Sentinel Hub first (preferred)
      try {
        const sentinelHealth = await sentinelHubService.checkHealth();
        if (sentinelHealth.authenticated) {
          setServiceHealth(sentinelHealth);
          setImageSource('sentinel');
          console.log('✅ Using Sentinel Hub for high-quality images');
          return;
        }
      } catch (sentinelError) {
        console.log('⚠️ Sentinel Hub not available, falling back to Planet Labs');
      }
      
      // Fallback to Planet Labs
      const planetHealth = await satelliteComparisonService.checkHealth();
      setServiceHealth(planetHealth);
      setImageSource('planet');
      
      if (!planetHealth.authenticated) {
        showAlert('Satellite service authentication failed. Please check your API keys.', 'error');
      }
    } catch (err) {
      console.error('Health check failed:', err);
      showAlert('Failed to connect to satellite service', 'error');
    }
  };

  /**
   * Handle location selection from map
   */
  const handleLocationSelect = useCallback((location) => {
    console.log('📍 Location selected:', location);
    setSelectedLocation(location);
    setError(null);
    setStep(2); // Move to date selection
  }, []);

  /**
   * Handle date selection
   */
  const handleDateSubmit = useCallback((selectedDate1, selectedDate2) => {
    console.log('📅 Dates selected:', selectedDate1, selectedDate2);
    
    // Validate dates
    const validation = satelliteComparisonService.validateDateRange(selectedDate1, selectedDate2);
    if (!validation.valid) {
      showAlert(validation.errors.join(', '), 'warning');
      return;
    }

    setDate1(selectedDate1);
    setDate2(selectedDate2);
    setError(null);
    
    // Start comparison
    performComparison(selectedLocation, selectedDate1, selectedDate2);
  }, [selectedLocation, imageSource]);

  /**
   * Perform the satellite image comparison
   */
  const performComparison = async (location, d1, d2) => {
    try {
      setLoading(true);
      setError(null);
      setStep(3); // Move to results view

      console.log(`🚀 Starting comparison using ${imageSource}...`);

      let result;
      if (imageSource === 'sentinel') {
        // Use Sentinel Hub for high quality
        result = await sentinelHubService.compareImages(
          location.lat,
          location.lng,
          d1,
          d2,
          0.05
        );
      } else {
        // Fallback to Planet Labs
        result = await satelliteComparisonService.compareImages({
          latitude: location.lat,
          longitude: location.lng,
          date1: d1,
          date2: d2,
          bboxSize: 0.05
        });
      }

      console.log('✅ Comparison complete:', result);
      const data = imageSource === 'sentinel' ? result : result.comparison;
      setComparisonData(data);

      // Trigger AI analysis after images are loaded
      if (data && data.image1 && data.image2) {
        performAIAnalysis(data);
      }

    } catch (err) {
      console.error('❌ Comparison failed:', err);
      
      // Provide user-friendly error messages
      let errorMessage = err.message || 'Failed to compare images';
      
      // Check if it's the "no images found" error
      if (errorMessage.includes('Could not retrieve one or both images') || 
          errorMessage.includes('Sentinel Hub') || 
          errorMessage.includes('configure Sentinel Hub credentials')) {
        errorMessage = 'No satellite images available for the selected dates.\n\nPlease try different dates - images may not be available within ±7 days of your selection due to cloud cover or satellite orbit patterns.';
      }
      
      showAlert(errorMessage, 'warning');
      setStep(2); // Go back to date selection on error
    } finally {
      setLoading(false);
    }
  };

  /**
   * Perform AI analysis on the comparison images (Segmentation-based Primary Method)
   */
  const performAIAnalysis = async (data) => {
    try {
      setAiLoading(true);
      setAiError(null);

      console.log('🤖 Starting AI Segmentation Analysis...');

      // Calculate days between images
      const daysBetween = satelliteComparisonService.daysBetween(
        data.image1.actual_date,
        data.image2.actual_date
      );

      // Call AI segmentation analysis service (primary method)
      const analysisResult = await satelliteComparisonService.analyzeComparisonSegmentation({
        image1Base64: data.image1.preview_base64,
        image2Base64: data.image2.preview_base64,
        latitude: data.location.latitude,
        longitude: data.location.longitude,
        date1: data.image1.actual_date,
        date2: data.image2.actual_date,
        daysBetween: daysBetween
      });

      console.log('✅ AI Analysis received!');
      setAiAnalysis(analysisResult.analysis);

    } catch (err) {
      console.error('❌ AI Analysis failed:', err);
      setAiError(err.message || 'Failed to perform AI analysis');
      // Don't show error to user prominently - just log it
      // The UI will show a fallback message
    } finally {
      setAiLoading(false);
    }
  };

  /**
   * Reset the comparison
   */
  const handleReset = () => {
    setSelectedLocation(null);
    setDate1('');
    setDate2('');
    setComparisonData(null);
    setError(null);
    setStep(1);
    // Reset AI analysis
    setAiAnalysis(null);
    setAiLoading(false);
    setAiError(null);
  };

  /**
   * Go back to previous step
   */
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setError(null);
    }
  };

  /**
   * TEST FUNCTION - Load and compare test images
   * TEMPORARY - FOR MANAGER DEMONSTRATION ONLY
   */
  const handleTestComparison = async () => {
    try {
      setLoading(true);
      setError(null);
      setStep(3); // Jump to results view

      console.log('🧪 TEST: Loading test comparison images from backend...');

      // Fetch test images
      const testResult = await testComparisonService.getTestComparisonImages();
      const testData = testResult.comparison;

      console.log('✅ TEST: Test images loaded:', testData);
      setComparisonData(testData);

      // Trigger both AI analyses
      if (testData && testData.image1 && testData.image2) {
        performTestAIAnalysis(testResult);
      }

    } catch (err) {
      console.error('❌ TEST: Test comparison failed:', err);
      showAlert(err.message || 'Failed to load test comparison', 'error');
      setStep(1); // Go back to location selection on error
    } finally {
      setLoading(false);
    }
  };

  /**
   * Perform AI analysis on test images (Segmentation-based Primary Method)
   * TEMPORARY - FOR MANAGER DEMONSTRATION ONLY
   */
  const performTestAIAnalysis = async (testResult) => {
    try {
      setAiLoading(true);
      setAiError(null);
      console.log('🧪 TEST: Starting segmentation AI analysis...');
      
      const analysisResult = await testComparisonService.analyzeTestSegmentation(testResult);
      console.log('✅ TEST: Segmentation analysis received!');
      setAiAnalysis(analysisResult);
    } catch (err) {
      console.error('❌ TEST: Segmentation analysis failed:', err);
      setAiError(err.message || 'Failed to perform AI analysis');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div 
      className="temporal-comparison-page"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/images/satellite-comparison.jpg)`,
      }}>
      {/* Header */}
      <header className="comparison-header">
        <button 
          className="back-button"
          onClick={() => navigate('/')}
          aria-label="Back to home"
        >
          <FaHome /> Back To Home
        </button>
        <div className="header-content">
          <div className="title-icon-wrapper">
            <FaTree className="title-icon" />
            <h1 className="page-title">
              Temporal Forest Monitoring
            </h1>
            <FaLeaf className="title-icon leaf-icon" />
          </div>
          <p className="page-subtitle" style={{color: 'white'}}>
            Compare satellite imagery to track vegetation changes across Texas landscapes
          </p>
        </div>
        {serviceHealth && (
          <div className={`health-indicator ${serviceHealth.authenticated ? 'healthy' : 'error'}`}>
            <span className="health-dot"></span>
            <FaSatellite className="health-icon" />
            {serviceHealth.authenticated ? 'Connected' : 'Disconnected'}
          </div>
        )}
      </header>


      {/* Progress indicator */}
      <div className="progress-steps">
        <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <div className="step-icon-wrapper">
            {step > 1 ? <FaCheckCircle className="step-icon" /> : <FaMapMarkerAlt className="step-icon" />}
          </div>
          <div className="step-label">Select Location</div>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <div className="step-icon-wrapper">
            {step > 2 ? <FaCheckCircle className="step-icon" /> : <FaCalendarAlt className="step-icon" />}
          </div>
          <div className="step-label">Choose Dates</div>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${step >= 3 ? 'active' : ''}`}>
          <div className="step-icon-wrapper">
            {step >= 3 ? <FaChartBar className="step-icon" /> : <FaSatellite className="step-icon" />}
          </div>
          <div className="step-label">View Comparison</div>
        </div>
      </div>

      {/* Main content */}
      <div className="comparison-content">
        
        {/* Step 1: Location Selection */}
        {step === 1 && (
          <div className="step-container fade-in">
            <div className="step-instructions">
              <FaMapMarkerAlt className="instruction-icon" />
              <h2>Step 1: Select a Location</h2>
              <p>Click anywhere on the Texas map to select a location for vegetation comparison</p>
              
              {/* TEST BUTTON - TEMPORARY FOR DEMONSTRATION */}
              <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                borderRadius: '12px',
                border: '2px solid #e65100',
                textAlign: 'center'
              }}>
                <p style={{ 
                  color: 'white', 
                  fontWeight: 'bold', 
                  marginBottom: '10px',
                  fontSize: '14px'
                }}>
                   Test Encroachment Detection Logic
                </p>
                <button
                  onClick={handleTestComparison}
                  style={{
                    padding: '12px 24px',
                    background: 'white',
                    color: '#e65100',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                  }}
                >
                  <FaSatellite style={{ marginRight: '8px' }} />
                  Run Test Comparison
                </button>
                <p style={{ 
                  color: 'white', 
                  fontSize: '12px', 
                  marginTop: '8px',
                  opacity: 0.9
                }}>
                  Compares pre-stored before/after images with full AI analysis
                </p>
              </div>
            </div>
            <LocationSelector 
              onLocationSelect={handleLocationSelect}
              selectedLocation={selectedLocation}
            />
          </div>
        )}

        {/* Step 2: Date Selection */}
        {step === 2 && selectedLocation && (
          <div className="step-container fade-in">
            <div className="step-instructions">
              <FaCalendarAlt className="instruction-icon" />
              <h2>Step 2: Select Two Dates</h2>
              <p>
                Choose two dates to compare imagery at {' '}
                <strong>
                  ({selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)})
                </strong>
              </p>
            </div>
            <DateSelector
              location={selectedLocation}
              onDateSubmit={handleDateSubmit}
              onBack={handleBack}
            />
          </div>
        )}

        {/* Step 3: Comparison Results */}
        {step === 3 && (
          <>
            {loading ? (
              <div className="loading-overlay">
                {/* <div className="spinner-large"></div> */}
                <FaSatellite className="loading-icon" />
                <h3>Fetching Satellite Imagery...</h3>
                <p>Analyzing vegetation patterns across time</p>
              </div>
            ) : comparisonData ? (
              <div className="results-container fade-in">
                <div className="results-header">
                  <div className="results-info">
                    <FaChartBar className="results-icon" />
                    <div>
                      <h2>Comparison Results</h2>
                      <p className="location-info">
                        <FaMapMarkerAlt className="inline-icon" />
                        Location: ({comparisonData.location.latitude.toFixed(4)}, {' '}
                        {comparisonData.location.longitude.toFixed(4)})
                      </p>
                    </div>
                  </div>
                  <button 
                    className="reset-button"
                    onClick={handleReset}
                  >
                    <FaRedo /> New Comparison
                  </button>
                </div>

                <ComparisonStats data={comparisonData} />

                <ComparisonViewer 
                  data={comparisonData} 
                  aiAnalysis={aiAnalysis}
                  aiLoading={aiLoading}
                  aiError={aiError}
                />
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="comparison-footer">
        <div className="footer-content">
          <div className="footer-info">
            <FaSatellite className="footer-icon" />
            <span>Powered by advanced satellite imagery technology</span>
          </div>
          <div className="footer-links">
            <FaLeaf className="footer-leaf-icon" />
            <span className="footer-tagline">Monitoring Earth's Green Spaces</span>
          </div>
        </div>
      </footer>

      {/* Custom Alert Modal */}
      {alertConfig && (
        <CustomAlert 
          message={alertConfig.message}
          type={alertConfig.type}
          onClose={closeAlert}
        />
      )}
    </div>
  );
};

export default TemporalComparisonPage;

